import { Check, Copy } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslations } from "use-intl";

import { Button } from "@lawline/ui/components/button";

// The button carries its own label, so it needs no tooltip. A screen reader does not read out a
// changed label on its own, so the status region announces the result.
export function CopyButton({ value, label }: { value: string; label: string }) {
	const t = useTranslations("common.copy");
	const [state, setState] = useState<"idle" | "copied" | "failed">("idle");

	useEffect(() => {
		if (state !== "copied") return;
		const timer = setTimeout(() => setState("idle"), 2000);
		return () => clearTimeout(timer);
	}, [state]);

	async function copy() {
		try {
			await navigator.clipboard.writeText(value);
			setState("copied");
		} catch {
			setState("failed");
		}
	}

	return (
		<span className="inline-flex flex-col items-end gap-1">
			<Button type="button" variant="outline" size="sm" onClick={copy}>
				{state === "copied" ? <Check /> : <Copy />}
				{state === "copied" ? t("copied") : label}
			</Button>
			<span role="status" className={state === "failed" ? "text-destructive text-xs" : "sr-only"}>
				{state === "copied" ? t("copied") : state === "failed" ? t("failed") : ""}
			</span>
		</span>
	);
}
